import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { testimonials } from '@/mocks/comics';
import { generateWebPageSchema, generateBreadcrumbListSchema, generateFAQPageSchema } from '@/utils/seo';

export default function AboutPage() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname]);

  const pageSchema = generateWebPageSchema(
    '关于韩国漫画网 - 免费韩漫在线阅读平台',
    '了解韩国漫画网的品牌故事、核心优势和联系方式。专业的免费韩漫在线阅读平台，提供完整的漫画大全与无缝在线观看服务。',
    'https://www.example.com/about'
  );
  const breadcrumbSchema = generateBreadcrumbListSchema([
    { name: '首页', url: 'https://www.example.com/' },
    { name: '关于我们', url: 'https://www.example.com/about' },
  ]);
  const faqSchema = generateFAQPageSchema([
    { q: '韩国漫画网是否需要注册？', a: '不需要注册即可免费阅读所有漫画，我们致力于为读者提供最便捷的阅读体验。' },
    { q: '漫画更新频率是怎样的？', a: '我们每日同步更新韩国最新连载漫画，确保您能第一时间看到最新章节。热门作品通常会在韩国更新后24小时内同步上线。' },
    { q: '可以在手机上阅读吗？', a: '当然可以！韩国漫画网完美适配移动端设备，您可以在手机浏览器上流畅阅读所有漫画，获得与APP相当的体验。' },
    { q: '漫画画质如何？', a: '我们提供的所有漫画均支持高清模式，保证文字清晰、画面精致，给您最好的视觉享受。' },
    { q: '如何查找特定漫画？', a: '您可以通过首页的分类导航浏览不同题材的漫画，也可以使用搜索功能快速找到您想看的作品。' },
  ]);

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(pageSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <Navbar />
      <main className="flex-1">
        <div className="bg-background-100 py-10 md:py-14 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-4" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700 font-medium">关于我们</span>
            </nav>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">关于韩国漫画网</h1>
            <p className="text-sm text-foreground-500 mt-2">您值得信赖的免费韩漫阅读平台</p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <section className="mb-10">
                <h2 className="text-lg font-bold text-foreground-900 font-heading mb-4">品牌故事</h2>
                <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                  <p>
                    <strong>韩国漫画网</strong>成立于2023年，是国内领先的<strong>免费韩漫在线阅读</strong>平台。我们怀揣着对韩国漫画的热爱和对读者的尊重，致力于打造一个内容丰富、体验流畅、完全免费的<strong>韩漫阅读</strong>社区。
                  </p>
                  <p>
                    从最初的几十部作品到如今的<strong>漫画大全</strong>，韩国漫画网始终坚持"以读者为中心"的理念。我们的团队由一群热爱漫画的年轻人组成，每天精心筛选和更新最新、最热门的<strong>韩国漫画</strong>资源，确保每一位访问者都能找到自己喜爱的作品。
                  </p>
                  <p>
                    我们提供的<strong>韩漫完整在线观看</strong>服务支持多种设备访问，无论您使用手机、平板还是电脑，都能享受高清流畅的阅读体验。网站界面简洁清爽，分类清晰明了，让您的<strong>在线观看</strong>之旅更加愉悦。
                  </p>
                  <p>
                    韩国漫画网不仅仅是一个<strong>免费网站</strong>，更是一个汇聚了万千漫友的温馨社区。我们相信，好的故事值得被更多人看到，好的作品值得被更多人欣赏。未来，我们将继续努力，为大家带来更多优质的韩漫内容和更好的阅读体验。
                  </p>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="text-lg font-bold text-foreground-900 font-heading mb-4">核心优势</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { icon: 'ri-shield-check-line', title: '完全免费', desc: '所有漫画资源免费阅读，无需付费订阅' },
                    { icon: 'ri-flashlight-line', title: '极速更新', desc: '每日同步韩国最新连载，快人一步' },
                    { icon: 'ri-smartphone-line', title: '多端适配', desc: '完美适配手机、平板、电脑等设备' },
                    { icon: 'ri-file-list-3-line', title: '海量资源', desc: '涵盖恋爱、奇幻、动作等多种题材' },
                    { icon: 'ri-translate', title: '精良翻译', desc: '专业翻译团队，保证内容准确流畅' },
                    { icon: 'ri-eye-line', title: '高清画质', desc: '所有漫画支持高清模式阅读体验' },
                  ].map((item) => (
                    <div key={item.title} className="flex gap-3 p-4 bg-background-100 rounded-lg">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                        <i className={`${item.icon} text-lg`}></i>
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground-800">{item.title}</h3>
                        <p className="text-xs text-foreground-500 mt-1">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section className="mb-10">
                <h2 className="text-lg font-bold text-foreground-900 font-heading mb-4">常见问题</h2>
                <div className="space-y-3">
                  {[
                    { q: '韩国漫画网是否需要注册？', a: '不需要注册即可免费阅读所有漫画，我们致力于为读者提供最便捷的阅读体验。' },
                    { q: '漫画更新频率是怎样的？', a: '我们每日同步更新韩国最新连载漫画，确保您能第一时间看到最新章节。热门作品通常会在韩国更新后24小时内同步上线。' },
                    { q: '可以在手机上阅读吗？', a: '当然可以！韩国漫画网完美适配移动端设备，您可以在手机浏览器上流畅阅读所有漫画，获得与APP相当的体验。' },
                    { q: '漫画画质如何？', a: '我们提供的所有漫画均支持高清模式，保证文字清晰、画面精致，给您最好的视觉享受。' },
                    { q: '如何查找特定漫画？', a: '您可以通过首页的分类导航浏览不同题材的漫画，也可以使用搜索功能快速找到您想看的作品。' },
                  ].map((faq, index) => (
                    <details key={index} className="group bg-background-100 rounded-lg">
                      <summary className="flex items-center justify-between p-4 cursor-pointer text-sm font-medium text-foreground-800">
                        {faq.q}
                        <i className="ri-arrow-down-s-line group-open:rotate-180 transition-transform duration-200"></i>
                      </summary>
                      <div className="px-4 pb-4">
                        <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                      </div>
                    </details>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="text-lg font-bold text-foreground-900 font-heading mb-4">联系我们</h2>
                <div className="p-5 bg-background-100 rounded-lg">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                        <i className="ri-mail-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-400">电子邮箱</p>
                        <p className="text-sm text-foreground-700">contact@koreancomic.cn</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                        <i className="ri-customer-service-2-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-400">客服热线</p>
                        <p className="text-sm text-foreground-700">400-123-4567</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                        <i className="ri-time-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-400">工作时间</p>
                        <p className="text-sm text-foreground-700">周一至周五 9:00-18:00</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                        <i className="ri-map-pin-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-400">公司地址</p>
                        <p className="text-sm text-foreground-700">中国上海市浦东新区张江高科技园区</p>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-foreground-400 leading-relaxed">
                    如果您有任何问题、建议或合作意向，欢迎通过以上方式联系我们。我们的团队将尽快回复您的消息。同时，如果您发现网站上有任何侵犯您权益的内容，请及时与我们联系，我们将在第一时间处理。
                  </p>
                </div>
              </section>
            </div>

            <div className="lg:col-span-1">
              <div className="sticky top-20 space-y-6">
                <div className="p-5 bg-background-100 rounded-lg">
                  <h3 className="text-sm font-semibold text-foreground-800 mb-4">用户口碑</h3>
                  <div className="space-y-4">
                    {testimonials.slice(0, 4).map((item) => (
                      <div key={item.id} className="pb-4 border-b border-background-200 last:border-0 last:pb-0">
                        <div className="flex items-center gap-1 mb-2">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <i
                              key={i}
                              className={`text-xs ${i < item.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                            ></i>
                          ))}
                        </div>
                        <p className="text-xs text-foreground-600 leading-relaxed">{item.comment}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 text-[10px] font-medium">
                            {item.name.charAt(0)}
                          </div>
                          <span className="text-xs text-foreground-500">{item.name}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="p-5 bg-primary-50 rounded-lg">
                  <h3 className="text-sm font-semibold text-foreground-800 mb-2">开始你的韩漫之旅</h3>
                  <p className="text-xs text-foreground-600 mb-4">海量免费韩漫等你来发现！</p>
                  <Link
                    to="/category/all"
                    className="inline-block w-full text-center px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
                  >
                    立即浏览漫画大全
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}